import { useLoader } from '../../../hooks/useLoader';
import { useNavigate, useParams } from 'react-router-dom';
import { useSwobNotifications } from '../../../hooks/NotificationsProvider';
import { FormProvider, Controller, FieldErrors } from 'react-hook-form';
import { FormValues, useCnisEndpointForm } from './cnis-endpoint-form';
import { useEffect, useState } from 'react';
import ReactEDSWrapper from '../../../utils/react-eds-wrapper';
import { Input, Tooltip, Button, TextArea, Select, MenuItem } from '@dsi/react-eds';
import styles from './CnisEndpointForm.module.css';
import api from '../../../api/axiosInstance';
import { CnisEndpoint, EnvironmentCategory } from './cnis-endpoint-interfaces';
import { useTheme } from '../../../hooks/useTheme';

const CnisEndpointForm = () => {
    const { theme } = useTheme();
    const { showLoader, hideLoader } = useLoader();
    const { addNotifications } = useSwobNotifications();
    const navigate = useNavigate();
    const [showPass, setShowPass] = useState(false);
    const params = useParams<{ id: string }>();
    const [formValues, setFormValues] = useState<CnisEndpoint>();

    const categoryOptions: EnvironmentCategory[] = ['Production', 'Staging', 'Development', 'Testing'];

    const form = useCnisEndpointForm();

    useEffect(() => {
        const fetchCnisEndpointData = async () => {
            if (params.id) {
                const response = await api.get(`/api/cnis-endpoint/${params.id}`);
                const initialValues = setInitialValues(response.data);
                setFormValues(response.data);
                form.reset(initialValues);
            }
        }
        fetchCnisEndpointData();
    }, [params.id]);

    function setInitialValues(setting: CnisEndpoint | null | undefined): FormValues | undefined {
        if (setting) {
          return {
            name: setting.name || '',
            category: setting.category || '',
            description: setting.description || '',
            sftpIp: setting.endpoint || '',
            sftpUser: setting.user || '',
            sftpPassword: setting.password || '',
            sftpPath: setting.sftpPath || ''
          }
        }
        return undefined;
    }

    const handleCancel = () => {
      navigate('/integrations/cnis');
    }

    const handleSave = () => {
        try {
          form.handleSubmit(() => {
            const dataToSend: CnisEndpoint = setCnisEndpoint();
            saveData(dataToSend);
            navigate('/integrations/cnis');
          }, (errors: FieldErrors<FormValues>) => {
            console.error('FORM ERRORS: ', errors);
          })();
        } catch {
          addNotifications([
            {
              title: 'Error',
              icon: 'icon-triangle-warning',
              description: `An unexpected error occurred while saving CNIS CSS Endpoint.`,
              timestamp: new Date(),
              color: 'red',
            },
          ]);
        }
    };

    const setCnisEndpoint = (): CnisEndpoint => {
      const values = form.getValues();
      console.log('VALUES', values);
      return {
        id: formValues?.id,
        name: values.name,
        description: values.description || '',
        category: categoryOptions.find(c => c === values.category) || 'Development',
        endpoint: values.sftpIp,
        user: values.sftpUser,
        password: values.sftpPassword,
        sftpPath: values.sftpPath
      }
    }

    const saveData = async(data: CnisEndpoint) => {
      showLoader();
      try {
        let response;
        if (data.id) {
            response = await api.put('/api/cnis-endpoint', data, {headers: { 'Content-Type': 'application/json' }});
        } else {
            response = await api.post('/api/cnis-endpoint', data, {headers: { 'Content-Type': 'application/json' }});
        }
        if (response) {
          addNotifications([
            {
              title: data.id ? `${data.name} edited successfully` : 'CNIS CSS Endpoint Successfully Created',
              icon: 'icon-success',
              description: data.id ? 'CNIS CSS Endpoint has been successfully edited.': 'CNIS CSS Endpoint has been successfully created and is ready to use.',
              timestamp: new Date(),
              color: 'green',
            },
          ]);
        }
        hideLoader();
      } catch (error) {
        console.error('Error while saving Cnis Endpoint settings', error);
        hideLoader();
        addNotifications([
          {
            title: 'Error',
            icon: 'icon-triangle-warning',
            description: `An unexpected error occurred while saving CNIS CSS Endpoint.`,
            timestamp: new Date(),
            color: 'red',
          },
        ]);
      }
    }

    return (
        <div className={`${styles.container} ${styles.containerPadding} ${theme ? styles.containerLight : styles.containerDark}`}>
            <div className="header">
                <span className={styles.title}>{params.id ? 'Edit': 'Create'} CNIS Endpoint</span>
            </div>
            <p className={styles.subtitle}>
                Set up a CNIS CSS Endpoint by defining its information, connection detail and SFTP settings.
            </p>
            <form id={"cnis-endpoint-form"}>
                <FormProvider {...form}>
                <div className={`${styles.formContainer} ${styles.allWidthTextArea}`}>
                    <div className='row'>
                        <div className={`column xl-3 lg-3 md-6 sm-12 ${styles.inputSpacing}`}>
                            <label htmlFor={"name"} className={styles.whiteText}>
                            Name <span className={styles.requiredAsterisk}> *</span>
                            <ReactEDSWrapper component={Tooltip} message="Enter the full URL for the CNIS connection." pointer>
                                <span className={styles.infoIcon}>
                                <i className="icon icon-info" />
                                </span>
                            </ReactEDSWrapper>
                            </label>
                            <Controller
                            name="name"
                            control={form.control}
                            render={({ field }) => (
                                <ReactEDSWrapper
                                component={Input}
                                id={"name"}
                                className={form.formState.errors.name ? `${styles.inputField} ${styles.inputError}` : styles.inputField}
                                type="text"
                                placeholder="Name"
                                {...field}
                                />
                            )}
                            />
                            {form.formState.errors.name && (
                            <span className={styles.error}>
                                <span className={styles.dangerIcon} aria-label="error">&#9888;</span>
                                {form.formState.errors.name.message as string}
                            </span>
                            )}
                        </div>
                        <div className={`column xl-3 lg-3 md-6 sm-12 ${styles.inputSpacing}`}>
                            <label htmlFor={"cnis-category"} className={styles.whiteText}>
                            Environment <span className={styles.requiredAsterisk}> *</span>
                            <ReactEDSWrapper component={Tooltip} message="Select the environment this endpoint belongs to. This can be changed later." pointer>
                                <span className={styles.infoIcon}>
                                <i className="icon icon-info" />
                                </span>
                            </ReactEDSWrapper>
                            </label>
                            <Controller
                                name="category"
                                control={form.control}
                                render={({ field }) => (
                                <div>
                                    <ReactEDSWrapper
                                    component={Select}
                                    className={`${styles.allWidthSelect}`}
                                    id="cnis-category"
                                    value={field.value}
                                    onChange={(value: any) => {
                                        const stringValue = Array.isArray(value) ? value[0] : value;
                                        field.onChange(stringValue);
                                    }}
                                    >
                                    {categoryOptions.map((cat) => (
                                        <ReactEDSWrapper component={MenuItem} key={cat} value={cat}>
                                        {cat}
                                        </ReactEDSWrapper>
                                    ))}
                                    </ReactEDSWrapper>
                                </div>
                                )}
                            />
                            {form.formState.errors.category && (
                            <span className={styles.error}>
                                <span className={styles.dangerIcon} aria-label="error">&#9888;</span>
                                {form.formState.errors.category.message as string}
                            </span>
                            )}
                        </div>
                    </div>
                    <div className='row'>
                        <div className={`column xl-6 lg-6 md-12 sm-12 ${styles.inputSpacing}`}>
                            <label htmlFor={"cnis-description"} className={styles.whiteText}>
                            Description</label>
                            <Controller
                                name="description"
                                control={form.control}
                                render={({ field }) => (
                                <ReactEDSWrapper
                                    component={TextArea}
                                    id="cnis-description"
                                    rows={3}
                                    placeholder="Describe the purpose of this CNIS Endpoint..."
                                    {...field}
                                />
                                )}
                            />
                        </div>
                    </div>
                    
                    <div className={styles.methodTitle}>SFTP Configuration</div>
                    <div className='row'>
                        <div className={`column xl-6 lg-6 md-12 sm-12 ${styles.inputSpacing}`}>
                            <label htmlFor={"sftpIp"} className={styles.whiteText}>
                            SFTP IP <span className={styles.requiredAsterisk}> *</span>
                            <ReactEDSWrapper component={Tooltip} message="Please enter a valid IPv4 address." pointer>
                                <span className={styles.infoIcon}>
                                <i className="icon icon-info" />
                                </span>
                            </ReactEDSWrapper>
                            </label>
                            <Controller
                            name="sftpIp"
                            control={form.control}
                            render={({ field }) => (
                                <ReactEDSWrapper
                                component={Input}
                                id={"sftpIp"}
                                className={form.formState.errors.sftpIp ? `${styles.inputField} ${styles.inputError}` : styles.inputField}
                                type="text"
                                placeholder="192.168.1.1"
                                {...field}
                                />
                            )}
                            />
                            {form.formState.errors.sftpIp && (
                            <span className={styles.error}>
                                <span className={styles.dangerIcon} aria-label="error">&#9888;</span>
                                {form.formState.errors.sftpIp.message as string}
                            </span>
                            )}
                        </div>
                    </div>
                    <div className='row'>
                        <div className={`column xl-6 lg-6 md-12 sm-12 ${styles.inputSpacing}`}>
                            <label htmlFor={"sftpUser"} className={styles.whiteText}>
                            User Name <span className={styles.requiredAsterisk}> *</span>
                            </label>
                            <Controller
                            name="sftpUser"
                            control={form.control}
                            render={({ field }) => (
                                <ReactEDSWrapper
                                component={Input}
                                id={"sftpUser"}
                                className={form.formState.errors.sftpUser ? `${styles.inputField} ${styles.inputError}` : styles.inputField}
                                type="text"
                                placeholder="User Name"
                                {...field}
                                />
                            )}
                            />
                            {form.formState.errors.sftpUser && (
                            <span className={styles.error}>
                                <span className={styles.dangerIcon} aria-label="error">&#9888;</span>
                                {form.formState.errors.sftpUser.message as string}
                            </span>
                            )}
                        </div>
                    </div>
                    <div className='row'>
                        <div className={`column xl-6 lg-6 md-12 sm-12 ${styles.inputSpacing}`}>
                            <label htmlFor={"sftpPassword"} className={styles.whiteText}>
                            Password <span className={styles.requiredAsterisk}> *</span>
                            </label>
                            <Controller
                            name="sftpPassword"
                            control={form.control}
                            render={({ field }) => (
                                <ReactEDSWrapper
                                component={Input}
                                id={"sftpPassword"}
                                className={form.formState.errors.sftpPassword ? `${styles.inputField} ${styles.inputError}` : styles.inputField}
                                icon={`icon ${showPass ? "icon-invisible" : "icon-eye"}`}
                                onIconClick={() => setShowPass(!showPass)}
                                type={showPass ? "text" : "password"}
                                placeholder="Password"
                                {...field}
                                />
                            )}
                            />
                            {form.formState.errors.sftpPassword && (
                            <span className={styles.error}>
                                <span className={styles.dangerIcon} aria-label="error">&#9888;</span>
                                {form.formState.errors.sftpPassword.message as string}
                            </span>
                            )}
                        </div>
                    </div>
                    <div className='row'>
                        <div className={`column xl-6 lg-6 md-12 sm-12 ${styles.inputSpacing}`}>
                            <label htmlFor={"sftpPath"} className={styles.whiteText}>
                            SFTP Path <span className={styles.requiredAsterisk}> *</span>
                            <ReactEDSWrapper component={Tooltip} message="Enter the file path of the SFTP server." pointer>
                                <span className={styles.infoIcon}>
                                <i className="icon icon-info" />
                                </span>
                            </ReactEDSWrapper>
                            </label>
                            <Controller
                            name="sftpPath"
                            control={form.control}
                            render={({ field }) => (
                                <ReactEDSWrapper
                                component={Input}
                                id={"sftpPath"}
                                className={form.formState.errors.sftpPath ? `${styles.inputField} ${styles.inputError}` : styles.inputField}
                                type={"text"}
                                placeholder="SFTP Path"
                                {...field}
                                />
                            )}
                            />
                            {form.formState.errors.sftpPath && (
                            <span className={styles.error}>
                                <span className={styles.dangerIcon} aria-label="error">&#9888;</span>
                                {form.formState.errors.sftpPath.message as string}
                            </span>
                            )}
                        </div>
                    </div>
                    <div className={`row ${styles.buttonContainer}`}>
                        <div className='lg-12'>
                            <ReactEDSWrapper
                            component={Button}
                            variant="primary"
                            className="btn secondary"
                            type="button"
                            onClick={handleCancel}
                            >
                            Cancel
                            </ReactEDSWrapper>
                            <ReactEDSWrapper
                            component={Button}
                            variant="primary"
                            className="btn primary"
                            type="button"
                            onClick={handleSave}
                            >
                            Save
                            </ReactEDSWrapper>
                        </div>
                    </div>
                </div>
                </FormProvider>
            </form>
        </div>
    )

}
export default CnisEndpointForm;